import { Button } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';

export interface ResolveSectionSelection {
  chip: string | null;
  customInput: string;
}

interface Props {
  title: string;
  options: string[];
  onConfirm: (value: string) => void;
  isLoading: boolean;
  disabled?: boolean;
  /** Controlled selection state */
  selection?: ResolveSectionSelection;
  /** Callback when selection changes */
  onSelectionChange?: (selection: ResolveSectionSelection) => void;
  /** Whether the confirm button should be disabled (for cross-section exclusivity) */
  isConfirmDisabled?: boolean;
}

export function ResolveSection(props: Props) {
  const {
    title,
    options,
    onConfirm,
    isLoading,
    disabled,
    selection,
    onSelectionChange,
    isConfirmDisabled,
  } = props;

  const selectedChip = selection?.chip ?? null;
  const customInput = selection?.customInput ?? '';

  const handleChipClick = (option: string) => {
    onSelectionChange?.({ chip: option, customInput: '' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectionChange?.({ chip: null, customInput: e.target.value });
  };

  const handleConfirm = () => {
    const value = selectedChip || customInput;
    if (value) {
      onConfirm(value);
    }
  };

  const hasSelection = selectedChip !== null || customInput.trim() !== '';
  const isConfirmEnabled = !disabled && !isConfirmDisabled && hasSelection;

  return (
    <div className="space-y-3">
      <h3 className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-semibold">
        {title}
      </h3>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => handleChipClick(option)}
            disabled={disabled || isLoading}
            className={clsx(
              'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
              'focus-visible:ring-primary-500 dark:ring-offset-dark-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              selectedChip === option
                ? 'border-primary-500 bg-primary-500 text-white'
                : 'border-onlight-line-3 dark:border-ondark-line-3 text-onlight-text-2 dark:text-ondark-text-2 hover:border-onlight-text-3 dark:hover:border-ondark-text-2 bg-transparent',
              (disabled || isLoading) && 'cursor-not-allowed opacity-50'
            )}
          >
            {option}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={customInput}
          onChange={handleInputChange}
          placeholder="Please Specify"
          disabled={disabled || isLoading}
          className={clsx(
            'dark:bg-dark-bg dark:text-dark-text dark:border-dark-border-surface focus:ring-primary-500 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1',
            (disabled || isLoading) && 'cursor-not-allowed opacity-50'
          )}
        />
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!isConfirmEnabled}
          loading={isLoading && isConfirmEnabled}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
}

export default ResolveSection;
